"use client"

import type React from "react"

import { useState } from "react"
import styles from "./NewTreatmentCard.module.css"
import { FiSettings, FiX } from "react-icons/fi"
import type { Treatment } from "API/Queries/useGetAllTreatments"
import { useFeatureFlipStore } from "Store/featureFlipStore"
import ConfigModal from "../ConfigModal/ConfigModal"

interface NewTreatment extends Treatment {
  color?: string
}

interface INewTreatmentCardProps {
  treatment: NewTreatment
  index: number
  handleTreatmentChange: (index: number, field: string, value: string | Record<string, string>, isNew?: boolean) => void
  handleRemoveTreatment: (index: number, isNew?: boolean) => void
}

const NewTreatmentCard = (props: INewTreatmentCardProps) => {
  const { treatment, index, handleTreatmentChange, handleRemoveTreatment } = props

  const [showConfigModal, setShowConfigModal] = useState(false)
  const { duplicateErrors } = useFeatureFlipStore()

  // Extract complex logic
  const configCount = treatment.configuration ? Object.keys(treatment.configuration).length : 0
  const validationErrorKey = `new_${index}`
  const hasDuplicateError = duplicateErrors[validationErrorKey]
  const inputClassName = `${styles.treatmentInput} ${hasDuplicateError ? styles.error : ""}`
  const configTooltipText = `${configCount} configuration${configCount !== 1 ? "s" : ""}`

  // Extract event handlers
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleTreatmentChange(index, "name", e.target.value, true)
  }

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleTreatmentChange(index, "description", e.target.value, true)
  }

  const handleConfigModalOpen = () => {
    setShowConfigModal(true)
  }

  const handleConfigModalClose = () => {
    setShowConfigModal(false)
  }

  const handleConfigChange = (updatedConfig: Record<string, string>) => {
    handleTreatmentChange(index, "configuration", updatedConfig, true)
  }

  const handleRemoveClick = () => {
    handleRemoveTreatment(index, true)
  }

  return (
    <>
      <div className={`${styles.treatmentItem} ${styles.newTreatment}`}>
        <div className={styles.treatmentItemContent}>
          <div className={styles.treatmentColor} style={{ background: treatment.color }} />
          <div className={styles.inputContainer}>
            <input
              type="text"
              value={treatment.name}
              onChange={handleNameChange}
              placeholder="Edit treatment name"
              className={inputClassName}
            />
          </div>
          <div className={styles.descriptionWrapper}>
            <input
              type="text"
              value={treatment.description || ""}
              onChange={handleDescriptionChange}
              placeholder="Add description"
              className={styles.treatmentInput}
            />
          </div>
          <div className={styles.buttonGroup}>
            <div className={styles.configButtonWrapper}>
              <button className={styles.configButton} onClick={handleConfigModalOpen}>
                <FiSettings />
              </button>
              {configCount > 0 && (
                <span className={styles.configBadge} title={configTooltipText}>
                  {configCount}
                </span>
              )}
            </div>
            <button className={styles.removeButton} onClick={handleRemoveClick}>
              <FiX />
            </button>
          </div>
        </div>
        {hasDuplicateError && <div className={styles.errorMessage}>{duplicateErrors[validationErrorKey]}</div>}
      </div>
      {showConfigModal && (
        <ConfigModal
          name={treatment.name}
          treatmentConfig={treatment.configuration}
          onClose={handleConfigModalClose}
          handleTreatmentChange={handleConfigChange}
        />
      )}
    </>
  )
}

export default NewTreatmentCard
